import React, { useEffect, useState } from 'react';
import { Link } from '@inertiajs/react';
import {
  PiPushPinSimpleFill,
  PiDotsThreeVerticalBold,
  PiTrashFill,
  PiTagBold,
} from 'react-icons/pi';
import classNames from 'classnames/bind';

import { Note } from '@/store/notesStore';
import PreviewNoteContent from '@/components/notes/preview/PreviewNoteContent';
import { useManageNote } from '@/api/notes/useManageNote';
import { usePinNote } from '@/api/notes/usePinNote';
import { useNotesListContext } from '@/contexts/NotesListContext';
import NoteTagsDisplay from '@/components/notes/tags/NoteTagsDisplay';
import NoteTagsEditor from '@/components/notes/tags/NoteTagsEditor';
import { highlightText } from '@/utils/searchHighlight';
import animStyles from './NoteAnimations.module.scss';

const cx = classNames.bind(animStyles);

interface CardProps {
  note: Note;
  searchQuery?: string;
  cardRef: React.RefObject<HTMLDivElement | null>;
  moreBtnRef: React.RefObject<HTMLButtonElement | null>;
  dropdownOpen: boolean;
  setDropdownOpen: (open: boolean) => void;
}

const Card: React.FC<CardProps> = ({
  note,
  searchQuery,
  cardRef,
  moreBtnRef,
  dropdownOpen,
  setDropdownOpen,
}) => {
  const { deleteNoteById } = useManageNote();
  const { togglePin } = usePinNote();
  const { fadingNoteId } = useNotesListContext();
  const dropdownRef = React.useRef<HTMLDivElement | null>(null);
  const [tagsOpen, setTagsOpen] = useState(false);

  const tags = note.tags ?? [];

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!dropdownOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        moreBtnRef.current &&
        !moreBtnRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen, setDropdownOpen, moreBtnRef]);

  const handleTogglePin = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setDropdownOpen(false);
    await togglePin(note);
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setDropdownOpen(false);

    // Show confirmation dialog
    const confirmed = window.confirm('Are you sure you want to delete this note? This action cannot be undone.');

    if (confirmed && note.id) {
      await deleteNoteById(note.id);
    }
  };

  return (
    <div
      className={cx(
        'relative rounded-xl shadow-md p-4 bg-white flex flex-col group w-full sm:max-w-[280px] transition-transform hover:scale-105',
        { fadeOut: fadingNoteId === note.id },
      )}
      style={{
        background: note.color || '#fff',
        minHeight: 100,
        height: Math.min(220 + (note.content.length * 0.5), 400),
      }}
      ref={cardRef}
    >
      <div className="flex justify-between items-start mb-3 pb-2 border-b border-gray-200/50">
        <Link
          href={`/app/note/${note.id}`}
          className={`flex-1 min-w-0 cursor-pointer border-l-3 pl-3 -ml-1 ${note.pinned ? 'border-yellow-500 dark:border-yellow-400' : 'border-transparent'}`}
        >
          <h3 className="font-bold text-base text-gray-950 dark:text-gray-900 line-clamp-2 pr-2 leading-tight">
            {note.title ? highlightText(note.title, searchQuery) : '‎'}
          </h3>
          {note.encrypted && <span className="text-[10px]" title={note.body_locked ? 'Encrypted and locked' : 'Encrypted and unlocked'} aria-label={note.body_locked ? 'Encrypted and locked' : 'Encrypted and unlocked'}>{note.body_locked ? '🔒 Locked' : '🔓 Encrypted'}</span>}
        </Link>
        <div className="relative z-50 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
          <button
            ref={moreBtnRef}
            type="button"
            className={`transition-opacity duration-150 p-1 rounded-full text-gray-600 cursor-pointer ${
              dropdownOpen 
                ? 'opacity-100 bg-gray-100 dark:bg-gray-200 text-gray-900' 
                : 'opacity-0 group-hover:opacity-100 hover:bg-gray-100 dark:hover:bg-gray-200 hover:text-gray-900'
            }`}
            title="More options"
            tabIndex={-1}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setDropdownOpen(!dropdownOpen);
            }}
          >
            <PiDotsThreeVerticalBold className="text-xl" />
          </button>
          {dropdownOpen && (
            <div
              ref={dropdownRef}
              className="absolute right-0 top-full mt-1 z-50 p-4"
              style={{ margin: '-2rem', padding: '2rem' }}
            >
              <div className="min-w-[180px] bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-2xl ring-1 ring-black ring-opacity-5">
                <button
                  type="button"
                  onClick={handleTogglePin}
                  className="w-full text-left px-4 py-2 flex items-center gap-2 text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 transition rounded-t-lg"
                >
                  <PiPushPinSimpleFill className={note.pinned ? 'text-yellow-500' : ''} />
                  {note.pinned ? 'Unpin Note' : 'Pin Note'}
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setDropdownOpen(false);
                    setTagsOpen(true);
                  }}
                  className="w-full text-left px-4 py-2 flex items-center gap-2 text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                >
                  <PiTagBold />Tags
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  className="w-full text-left px-4 py-2 flex items-center gap-2 text-gray-900 dark:text-gray-100 hover:bg-red-500 hover:text-white dark:hover:bg-red-600 transition rounded-b-lg"
                >
                  <PiTrashFill />Delete Note
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <Link
        href={`/app/note/${note.id}`}
        className="text-gray-600 dark:text-gray-700 text-[13px] mb-2 line-clamp-4 min-h-[48px] flex-1 leading-relaxed cursor-pointer"
      >
        <PreviewNoteContent note={note} searchQuery={searchQuery} />
      </Link>

      {/* Tags area — editor when open, read-only chips otherwise */}
      {tagsOpen ? (
        <div className="mt-auto pt-2" onClick={(e) => e.stopPropagation()}>
          <NoteTagsEditor
            noteId={note.id!}
            tags={tags}
            onClose={() => setTagsOpen(false)}
          />
        </div>
      ) : (
        tags.length > 0 && (
          <div
            className="mt-auto pt-2 cursor-pointer"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setTagsOpen(true); }}
            title="Click to edit tags"
          >
            <NoteTagsDisplay tags={tags} maxVisible={3} />
          </div>
        )
      )}
    </div>
  );
};

export default Card;
